import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { countries } from 'country-flags-svg-v2';
import { addDays, addWeeks, formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';
import Papa from 'papaparse';
import React from 'react';

import { ONE_DAY_IN_MS, ONE_MIN_IN_MS } from 'src/common/constants';
import type { CalculateDeadlineParams } from 'src/features/paths/components/auth-main-content/components/learner-scheduler/learner-scheduler.types';

import { UserRoles } from '../enums';
import { gb } from '../growthbook';
import type { UserData } from '../interfaces';
import { isQueryErrorWithMessage } from './slices';

export * from './certification';
export * from './get-rank-title';
export * from './pdfExtension';

export const recoveryCodesToText = (recoveryCodes: string[]) => {
  let text = '';
  for (const [index, recoveryCode] of recoveryCodes.entries()) {
    if (index === 0) {
      text = recoveryCode;
    } else if (index % 2 === 0) {
      text = `${text}\n${recoveryCode}`;
    } else {
      text = `${text} ${recoveryCode}`;
    }
  }
  return text;
};

export const convertDateToUTC = (date: Date | string) => {
  const newDate = new Date(date);
  return new Date(newDate.getTime() + newDate.getTimezoneOffset() * ONE_MIN_IN_MS);
};

export const isDeadlinePassed = (deadline: string) => {
  const nowUTC = convertDateToUTC(new Date());
  const deadlineUTC = convertDateToUTC(deadline);

  return nowUTC >= deadlineUTC;
};

export const getDifferenceInDays = (deadline: string) => {
  const nowUTC = convertDateToUTC(new Date());
  const deadlineUTC = convertDateToUTC(deadline);

  const diffInMs = deadlineUTC.getTime() - nowUTC.getTime();
  return Math.ceil(diffInMs / ONE_DAY_IN_MS);
};

export const extractB2BUserRoles = (user: UserData['user'] | undefined) => ({
  isCompanyAdmin: !!user?.companies?.length,
  isB2BUserActive: !!user?.subscriptions?.find((sub) => sub.type === 'company' && sub.status === 'active'),
});

export const B2BUserHelper = (user: UserData['user'] | undefined) => {
  const roles = extractB2BUserRoles(user);
  const subscription = user?.subscriptions?.find((sub) => sub?.type === 'company' && sub?.status === 'active');
  const hasB2BSubscription = !!subscription;
  const isBusinessUserOnly = roles.isB2BUserActive && !roles.isCompanyAdmin;
  const isAdminAndB2BUser = roles.isCompanyAdmin && hasB2BSubscription;
  const isAdminOnly = roles.isCompanyAdmin && !isBusinessUserOnly && !isAdminAndB2BUser;
  const activeCompany = user?.companies?.find((company) => !company.isDeadlinePassed);
  const companyName = subscription?.companyName ?? activeCompany?.name;
  const isEduAdminOnly = activeCompany?.type === 'edu' && !hasB2BSubscription;
  const isEduUser = subscription?.companyType === 'edu';

  function getCompanyId(): string | undefined {
    if (isAdminOnly) {
      return user?.companies?.[0]?._id;
    }

    if ((isAdminOnly || isAdminAndB2BUser) && activeCompany) {
      return activeCompany._id;
    }

    return undefined;
  }

  const companyId = getCompanyId();

  return {
    ...roles,
    subscription,
    isAdminAndB2BUser,
    isBusinessUserOnly,
    isAdminOnly,
    companyId,
    isBusinessUser: isBusinessUserOnly || isAdminOnly || isAdminAndB2BUser || hasB2BSubscription,
    isSubscriptionActive: hasB2BSubscription,
    companyName,
    isEduAdminOnly,
    isEduUser,
  };
};

export const isB2BUserOnly = (user: UserData['user'] | undefined) => {
  if (!user) return false;
  const { isCompanyAdmin, isB2BUserActive } = extractB2BUserRoles(user);
  return isB2BUserActive && !isCompanyAdmin;
};

export const generateRandomToken = () => {
  const randomBytes = new Uint8Array(20);
  crypto.getRandomValues(randomBytes);
  return [...randomBytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const findCountryNameByIso2Code = (iso2: string): string => {
  const country = countries.find((someCountry) => someCountry.iso2 === iso2.toUpperCase());
  return country?.name ?? '';
};

export const sanitizeAndRenderHtmlString = (htmlString: string) => {
  const sanitizedHTML = DOMPurify.sanitize(htmlString);
  return React.createElement('div', {
    dangerouslySetInnerHTML: { __html: sanitizedHTML },
  });
};

/**
 * Helper function to check if the string provided as the argument is a valid URL
 */
export const isValidURL = (str: string) => {
  const domainPattern = '(?<domain>[\\w-]+\\.)+[\\w]{2,}';
  const ipPattern = '(?<ip>(\\d{1,3}\\.){3}\\d{1,3})';
  const portPattern = '(:(?<port>\\d+))?';
  const pathPattern = '(?<path>/[\\w-./%+&:]+)*';
  const queryPattern = '(\\?(?<query>[\\w-./?%+&=:]+))?';
  const fragmentPattern = '(#(?<fragment>[\\w-./_%+&]+))?';

  const pattern = new RegExp(
    `^(https?://)?(${domainPattern}|${ipPattern})${portPattern}${pathPattern}${queryPattern}${fragmentPattern}$`,
    'i',
  );

  return pattern.test(str);
};

export const isValidImageUrl = (url: string) => {
  const isImageUrl = /(https?:\/\/.*\.(?:png|jpg|jpeg|gif|svg))/i.test(url);
  return isImageUrl;
};

/**
 * Redirects the browser to a specific path on the same domain. Support localhost as well as other envs
 * @param redirectPath path in URL
 * @param toReact include /r/ by default
 * @param keepHistory while preserving history in browser, if kept user can press return and come back to page
 */
export const redirectToPath = (redirectPath: string, keepHistory = false, toReact = true) => {
  const port = (window.location.port && (toReact ? `:${window.location.port}` : ':1337')) || ''; // support localhost
  const hostName = `${window.location.hostname}${port}/`;
  const includeReact = toReact ? 'r/' : '';
  const redirectUrl = `${window.location.protocol}//${hostName}${includeReact}${redirectPath}`;
  if (keepHistory) {
    window.location.assign(redirectUrl);
  } else {
    window.location.replace(redirectUrl);
  }
};

/**
 * Safely decodes text by using regular expression to replace any '%' that is not followed by two valid hex characters
 * then decodeURIComponent
 * @param text - string
 */
export const decode = (text: string) => {
  try {
    return decodeURIComponent(text.replace(/%(?![\dA-Fa-f]{2})/g, '%25'));
  } catch {
    return text;
  }
};

export const calculateEstimatedCompletionDate = ({
  totalPathHours,
  completedHours,
  hoursPerWeek,
}: CalculateDeadlineParams): Date => {
  const remainingHours = totalPathHours - completedHours;

  if (remainingHours <= 0 || hoursPerWeek <= 0) {
    return new Date();
  }

  const weeksNeeded: number = remainingHours / 60 / hoursPerWeek;
  const wholeWeeks = Math.floor(weeksNeeded);
  const fractionalWeeks = weeksNeeded - wholeWeeks;
  const daysNeeded = Math.round(fractionalWeeks * 7);

  let estimatedCompletionDate = addWeeks(new Date(), wholeWeeks);
  estimatedCompletionDate = addDays(estimatedCompletionDate, daysNeeded);

  return estimatedCompletionDate;
};

export const triggerDownload = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.setAttribute('download', '');
  document.body.append(link);
  link.click();
  link.remove();
};

export const formatPrice = (price?: number): [string | null, string | null, string | null] => {
  if (price === undefined) {
    return [null, null, null];
  }

  const int = Math.trunc(price).toString();
  const decimal = Number.isInteger(price) ? null : price.toFixed(2).split('.')[1];

  let formatted = int;
  if (decimal) {
    formatted += `.${decimal}`;
  }

  return [formatted, int, decimal ?? null];
};

/**
 * Formats a number with K, M, B suffixes for better readability
 * @param num - The number to format
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted string with appropriate suffix
 * @example
 * formatNumberWithSuffix(57000) // "57K"
 * formatNumberWithSuffix(1234567) // "1.2M"
 * formatNumberWithSuffix(2500000000) // "2.5B"
 */
export const formatNumberWithSuffix = (num: number, decimalPlaces = 1): string => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(decimalPlaces).replace(/\.0$/, '')}B`;
  }

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(decimalPlaces).replace(/\.0$/, '')}M`;
  }

  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(decimalPlaces).replace(/\.0$/, '')}K`;
  }

  return num.toString();
};

/**
 * Extracts an error message from an error object.
 * @param error - The error object (can be FetchBaseQueryError, Error, or any other type).
 * @param defaultMessage - The default message to return if no error message can be extracted.
 * @returns The extracted error message or the default message.
 */
export const getErrorMessage = (error: unknown, defaultMessage = 'Something went wrong'): string => {
  // Handle FetchBaseQueryError (RTK Query error)
  if (isFetchBaseQueryError(error)) {
    if (error.data && typeof error.data === 'object' && 'message' in error.data) {
      return error.data.message as string;
    }
    return defaultMessage;
  }

  // Handle Error objects (e.g., network errors)
  if (isErrorWithMessage(error)) {
    return error.message;
  }

  // Handle unknown errors
  return defaultMessage;
};

/**
 * Handles common error patterns and dispatches appropriate notifications
 * @param error - The error object to handle
 * @param defaultMessage - The default message to show for unknown errors
 */
export const handleErrorWithNotification = (
  error: unknown,
  defaultMessage = 'An unknown error has occurred.',
): void => {
  if (isQueryErrorWithMessage(error)) {
    NotificationDispatch('error', error.data.message);
    return;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { originalStatus?: number }).originalStatus === 429
  ) {
    NotificationDispatch('error', 'Too many requests, please try again later.');
    return;
  }

  NotificationDispatch('error', defaultMessage);
};

/**
 * Gets error message from common error patterns without dispatching notifications
 * @param error - The error object to handle
 * @param defaultMessage - The default message to return for unknown errors
 * @returns The appropriate error message string
 */
export const getErrorMessageFromCommonPatterns = (
  error: unknown,
  defaultMessage = 'An unknown error has occurred',
): string => {
  if (isQueryErrorWithMessage(error)) {
    return error.data.message;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { originalStatus?: number }).originalStatus === 429
  ) {
    return 'Too many requests, please try again later.';
  }

  return defaultMessage;
};

// Type guard to check if the error is a FetchBaseQueryError
export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error != null && 'status' in error;

// Type guard to check if the error has a `message` property
export const isErrorWithMessage = (error: unknown): error is { message: string } =>
  typeof error === 'object' && error != null && 'message' in error;

export const getSecureRandom = (min: number, max: number): number => {
  const { crypto } = window;
  const buffer: ArrayBufferLike = new ArrayBuffer(4);
  const array: Uint32Array = new Uint32Array(buffer);

  if (!crypto) {
    throw new Error('Crypto API not supported');
  }

  crypto.getRandomValues(array);

  // Add null check for array[0]
  const value = array[0];
  if (typeof value !== 'number') {
    throw new TypeError('Failed to generate random value');
  }

  const maxUint32 = 4_294_967_295;
  const randomNumber = value / (maxUint32 + 1);

  return Math.floor(randomNumber * (max - min)) + min;
};

export const capitalize = (str = '') => str.charAt(0).toUpperCase() + str.slice(1);

export const calculateDifferenceInPercentage = (value1: number, value2 = 0) => {
  const difference = value1 - value2;

  if (value2 === 0) {
    return value1 > value2 ? 100 : 0;
  }
  return Math.round((difference / value2) * 100);
};

export const pluralize = (word: string, count: number): string => {
  if (count === 1) return word;

  return `${word}s`;
};

// @TODO: Refactor - remove content-dev - https://tryhackme.atlassian.net/browse/PLAT-540
/**
 * @description Check if the user is a content dev.
 * This is feature-flag dependent for now as we are planning to remove the content dev role.
 * @param user - The user object.
 * @returns boolean - Whether the user is a content dev.
 */
export const isInternalContentDev = (user?: UserData['user']): boolean => {
  const shouldOnlyUseContentDevInternal = gb.isOn('is-content-dev-internal-enabled');

  if (!user?.roles) return false;

  if (shouldOnlyUseContentDevInternal) return user.roles.includes(UserRoles.CONTENT_DEV_INTERNAL);

  return user.roles.includes(UserRoles.CONTENT_DEV_INTERNAL);
};

export const isContentDev = (user?: UserData['user']): boolean => {
  if (!user?.roles) return false;

  return user.roles.includes(UserRoles.CONTENT_DEV_EXTERNAL) || user.roles.includes(UserRoles.CONTENT_DEV_INTERNAL);
};

export const hasAttackBoxAdminRole = (user?: UserData['user']): boolean => {
  if (!user?.roles) return false;

  return user.roles.includes(UserRoles.ATTACK_BOX_ADMIN);
};

export const isSocSimInternalQA = (user?: UserData['user']) => {
  if (!user?.roles) return false;

  return user.roles.includes(UserRoles.SOC_SIM_INTERNAL_QA);
};

export const isExternalContentDev = (user: UserData['user'] | undefined) =>
  user?.roles?.includes(UserRoles.CONTENT_DEV_EXTERNAL) ?? false;

export const getDomainFromEmail = (email: string) => {
  if (!!email && email.includes('@')) {
    return email.slice(Math.max(0, email.lastIndexOf('@') + 1));
  }

  return '';
};

/**
 * @description A utility function to create a promise that rejects after a specified timeout unless resolved first.
 * @param promise
 * @param timeoutMs
 */
export const promiseWithTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  // Create a timeout promise that rejects after specified milliseconds
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(new Error(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  // Race the original promise against the timeout
  return Promise.race([promise, timeoutPromise]);
};

export const prettifyDate = (date: string): string => {
  const trimmedDate = date.trim();
  const then = new Date(trimmedDate);
  if (Number.isNaN(then.getTime())) return '';

  return formatDistanceToNow(then);
};

export const exportToCSV = <T>(data: T[], fileName = 'export') => {
  try {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const today = new Date().toISOString().split('T')[0];
    a.download = `${fileName}-${today}.csv`;
    a.click();

    setTimeout(() => URL.revokeObjectURL(url), 500);
  } catch (error) {
    console.error('Error exporting to CSV', error);
  }
};

export const camelCaseToSentenceCase = (camelCaseString: string): string => {
  const regexResult = camelCaseString.replace(/([A-Z])/g, ' $1').toLowerCase();
  return regexResult.charAt(0).toUpperCase() + regexResult.slice(1);
};

export { downloadDashboard } from './dashboard-download';
